import axios from "axios"
import { NextResponse } from "next/server"
import { z } from "zod"
import { clearSession, getSession } from "@/services/auth-service"
import {
  getCustomerCpfData,
  updateCustomerCpfAndPhone,
} from "@/services/woo-commerce-service"
import {
  type AccountAddressMode,
  getAccountAddresses,
  updateAccountAddress,
  type WordPressAddressBlock,
  type WordPressAddressUpdatePayload,
} from "@/services/wordpress-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const modeSchema = z.enum(["billing", "shipping"])

const addressFormSchema = z.object({
  email: z.string().trim(),
  firstName: z.string().trim(),
  lastName: z.string().trim(),
  cpf: z.string().trim(),
  phone: z.string().trim(),
  zipCode: z.string().trim(),
  street: z.string().trim(),
  number: z.string().trim(),
  complement: z.string().trim(),
  district: z.string().trim(),
  city: z.string().trim(),
  state: z.string().trim(),
})

const updatePayloadSchema = z.object({
  mode: modeSchema,
  address: addressFormSchema,
})

interface AccountAddressFormData {
  email: string
  firstName: string
  lastName: string
  cpf: string
  phone: string
  zipCode: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
}

const getEmptyAccountAddressFormData = (): AccountAddressFormData => {
  return {
    email: "",
    firstName: "",
    lastName: "",
    cpf: "",
    phone: "",
    zipCode: "",
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
  }
}

const toAddressFormData = (
  mode: AccountAddressMode,
  block: WordPressAddressBlock | undefined,
  woocommerceData?: { cpf: string | null; phone: string | null } | null,
  fallback?: AccountAddressFormData
): AccountAddressFormData => {
  const number =
    mode === "billing"
      ? block?.billing_number?.trim()
      : block?.shipping_number?.trim()
  const district =
    mode === "billing"
      ? block?.billing_neighborhood?.trim()
      : block?.shipping_neighborhood?.trim()

  // Each mode has its own CPF and phone fields, fallback to WooCommerce customer data
  const cpf =
    mode === "billing"
      ? (block?.billing_cpf || block?.cpf || "").trim()
      : (block?.shipping_cpf || block?.cpf || "").trim()

  const phone =
    mode === "billing"
      ? (block?.billing_phone || block?.phone || "").trim()
      : (block?.shipping_phone || block?.phone || "").trim()

  return {
    email: block?.email?.trim() ?? "",
    firstName: block?.first_name?.trim() ?? "",
    lastName: block?.last_name?.trim() ?? "",
    cpf: cpf || woocommerceData?.cpf || fallback?.cpf || "",
    phone: phone || woocommerceData?.phone || fallback?.phone || "",
    zipCode: block?.postcode?.trim() ?? "",
    street: block?.address_1?.trim() ?? "",
    number: number ?? "",
    complement: block?.address_2?.trim() ?? "",
    district: district ?? "",
    city: block?.city?.trim() ?? "",
    state: block?.state?.trim() ?? "",
  }
}

const toWordPressAddressPayload = (
  mode: AccountAddressMode,
  address: AccountAddressFormData
): WordPressAddressUpdatePayload => {
  if (mode === "billing") {
    return {
      billing: {
        first_name: address.firstName,
        last_name: address.lastName,
        address_1: address.street,
        address_2: address.complement,
        city: address.city,
        state: address.state,
        postcode: address.zipCode,
        country: "BR",
        email: address.email,
        phone: address.phone,
        cpf: address.cpf,
        number: address.number,
        neighborhood: address.district,
      },
    }
  }

  return {
    shipping: {
      first_name: address.firstName,
      last_name: address.lastName,
      address_1: address.street,
      address_2: address.complement,
      city: address.city,
      state: address.state,
      postcode: address.zipCode,
      country: "BR",
      phone: address.phone,
      cpf: address.cpf,
      number: address.number,
      neighborhood: address.district,
    },
  }
}

const getSessionToken = async (): Promise<string | null> => {
  const session = await getSession()
  const token = session?.wordpressSessionCookie?.trim()

  return token || null
}

const toUnauthorizedResponse = () => {
  return NextResponse.json(
    {
      ok: false,
      code: "UNAUTHORIZED",
      message: "Sessao nao autenticada.",
    },
    { status: 401, headers: NO_CACHE_HEADERS }
  )
}

const isUnauthorizedAxiosError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false
  }

  const status = error.response?.status

  return status === 401 || status === 403
}

export const GET = async (request: Request) => {
  const url = new URL(request.url)
  const parsedMode = modeSchema.safeParse(url.searchParams.get("mode"))

  if (!parsedMode.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Parametro mode invalido para endereco da conta.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  const sessionToken = await getSessionToken()

  if (!sessionToken) {
    return toUnauthorizedResponse()
  }

  try {
    const session = await getSession()
    const addresses = await getAccountAddresses(sessionToken)
    const mode = parsedMode.data
    const addressBlock =
      mode === "billing" ? addresses.billing : addresses.shipping

    // Fetch from WooCommerce if WordPress doesn't return CPF/phone
    const woocommerceData = await fetchWooCommerceDataIfNeeded(
      mode,
      addressBlock,
      session?.user?.id
    )

    return NextResponse.json(
      {
        ok: true,
        address: toAddressFormData(mode, addressBlock, woocommerceData),
      },
      { headers: NO_CACHE_HEADERS }
    )
  } catch (error) {
    if (isUnauthorizedAxiosError(error)) {
      await clearSession()
      return toUnauthorizedResponse()
    }

    return NextResponse.json(
      {
        ok: false,
        code: "ADDRESS_FETCH_FAILED",
        message: "Nao foi possivel carregar o endereco da conta.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}

const fetchWooCommerceDataIfNeeded = async (
  mode: AccountAddressMode,
  addressBlock: WordPressAddressBlock | undefined,
  userId: number | string | undefined
): Promise<{ cpf: string | null; phone: string | null } | null> => {
  const cpfField =
    mode === "billing"
      ? addressBlock?.billing_cpf || addressBlock?.cpf
      : addressBlock?.shipping_cpf || addressBlock?.cpf
  const phoneField =
    mode === "billing"
      ? addressBlock?.billing_phone || addressBlock?.phone
      : addressBlock?.shipping_phone || addressBlock?.phone

  // Fetch from WooCommerce if either CPF or phone is missing
  if (!(cpfField && phoneField) && userId) {
    const fullData = await getCustomerCpfData(userId)
    return {
      cpf: mode === "billing" ? fullData.billing_cpf : fullData.shipping_cpf,
      phone:
        mode === "billing" ? fullData.billing_phone : fullData.shipping_phone,
    }
  }

  return null
}

export const POST = async (request: Request) => {
  const payload = await request.json().catch(() => null)
  const parsedPayload = updatePayloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para atualizar endereco da conta.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  const sessionToken = await getSessionToken()

  if (!sessionToken) {
    return toUnauthorizedResponse()
  }

  const { mode, address } = parsedPayload.data
  const wordPressPayload = toWordPressAddressPayload(mode, address)

  try {
    const session = await getSession()

    // Update WordPress address
    const updatedAddresses = await updateAccountAddress(
      sessionToken,
      mode,
      wordPressPayload
    )

    // Also update CPF and phone in WooCommerce if provided
    if ((address.cpf || address.phone) && session?.user?.id) {
      await updateCustomerCpfAndPhone(
        session.user.id,
        mode,
        address.cpf,
        address.phone
      )
    }

    const updatedAddressBlock =
      mode === "billing" ? updatedAddresses.billing : updatedAddresses.shipping

    // Fetch from WooCommerce if WordPress doesn't return CPF/phone
    const woocommerceData = await fetchWooCommerceDataIfNeeded(
      mode,
      updatedAddressBlock,
      session?.user?.id
    )

    return NextResponse.json(
      {
        ok: true,
        address: updatedAddressBlock
          ? toAddressFormData(
              mode,
              updatedAddressBlock,
              woocommerceData,
              address
            )
          : getEmptyAccountAddressFormData(),
      },
      { headers: NO_CACHE_HEADERS }
    )
  } catch (error) {
    if (isUnauthorizedAxiosError(error)) {
      await clearSession()
      return toUnauthorizedResponse()
    }

    return NextResponse.json(
      {
        ok: false,
        code: "ADDRESS_UPDATE_FAILED",
        message: "Nao foi possivel atualizar endereco da conta.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}
